import { QueryParams, TAG_TYPES } from 'types';
import { baseApi } from './index';
import { InventoryDocument, InventoryDocument as T } from 'models';
import { getQueryParams } from 'utils';
import { BaseInventoryDocument, InventoryDocumentResponse } from 'models/InventoryDocument';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/inventory-documents`;
const tagType = TAG_TYPES.InventoryCheckOrder;

export interface ResponseCheckOrderExport {
  statusCode: number;
  success: boolean;
  data: {
    filePath: string;
    fileUrl: string;
  };
}

const InventoryCheckOrderApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllInventoryCheckOrders: builder.query<
      {
        data: {
          rows: T[];
          count: number;
          kpis: {
            rpoDraft: number;
            rpoPaid: number;
          };
        };

        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getInventoryCheckOrderById: builder.query<
      {
        data: InventoryDocument;
        success: boolean;
      },
      { id: string; query: QueryParams }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${urlEndpoint}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: tagType, id: params.id }],
    }),

    createInventoryCheckOrder: builder.mutation<InventoryDocumentResponse, Partial<BaseInventoryDocument>>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data: {
          ...data,
          type: 'inventory-check-order',
          isPublishing: false,
        },
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
      ],
    }),

    updateInventoryCheckOrder: builder.mutation<
      InventoryDocumentResponse,
      Partial<BaseInventoryDocument> & { id: string }
    >({
      query: ({ id, ...patch }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),

    deleteInventoryCheckOrder: builder.mutation<
      {
        statusCode: number;
      },
      string
    >({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
    exportInventoryCheckOrder: builder.mutation<ResponseCheckOrderExport, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}/inventory-check-orders/exports`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllInventoryCheckOrdersQuery,
  useGetInventoryCheckOrderByIdQuery,
  useCreateInventoryCheckOrderMutation,
  useUpdateInventoryCheckOrderMutation,
  useDeleteInventoryCheckOrderMutation,
  useExportInventoryCheckOrderMutation,
} = InventoryCheckOrderApiService;
